import { useState, useEffect } from 'react';
import { usePendingTrips } from '../hooks';
import { useProjectOptions } from '@/hooks';
import { TripCard } from './TripCard';
import type { SelectOption } from '@/components/ui';

export function PendingTripsList() {
  const { trips, isLoading, error, assignToProject, dismissTrip, refetch } = usePendingTrips();
  const { projects } = useProjectOptions(['planned', 'in_progress']);

  const [selectedProjects, setSelectedProjects] = useState<Record<string, string>>({});
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Initialize selected projects with suggestions
  useEffect(() => {
    const initial: Record<string, string> = {};
    trips.forEach((trip) => {
      if (trip.suggestedProjectId && !selectedProjects[trip.id]) {
        initial[trip.id] = trip.suggestedProjectId;
      }
    });
    if (Object.keys(initial).length > 0) {
      setSelectedProjects((prev) => ({ ...prev, ...initial }));
    }
  }, [trips, selectedProjects]);

  const handleAssign = async (tripId: string) => {
    const projectId = selectedProjects[tripId];
    if (!projectId) return;

    setProcessingIds((prev) => new Set(prev).add(tripId));
    try {
      await assignToProject(tripId, projectId);
    } catch (err) {
      console.error('Error assigning trip:', err);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(tripId);
        return next;
      });
    }
  };

  const handleDismiss = async (tripId: string) => {
    setProcessingIds((prev) => new Set(prev).add(tripId));
    try {
      await dismissTrip(tripId);
    } catch (err) {
      console.error('Error dismissing trip:', err);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(tripId);
        return next;
      });
    }
  };

  // Build project options for a specific trip
  const buildProjectOptions = (trip: typeof trips[0]): SelectOption[] => {
    const options: SelectOption[] = [];

    // Add suggested project first if available
    if (trip.suggestedProjectId && trip.suggestedProjectName) {
      options.push({
        value: trip.suggestedProjectId,
        label: `✓ ${trip.suggestedProjectName}`,
        sublabel: trip.suggestedCustomerName || 'Suggested match',
      });
    }

    // Add other projects
    projects
      .filter((p) => p.id !== trip.suggestedProjectId)
      .forEach((project) => {
        options.push({
          value: project.id,
          label: project.name,
          sublabel: project.customerName || undefined,
        });
      });

    return options;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        <p>{error}</p>
        <button onClick={refetch} className="mt-2 text-sm underline hover:no-underline">
          Try again
        </button>
      </div>
    );
  }

  if (trips.length === 0) {
    return (
      <div className="text-center py-12 bg-secondary-50 rounded-lg">
        <svg
          className="w-12 h-12 mx-auto text-secondary-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="mt-2 text-secondary-600">All trips have been assigned!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary-600">
          {trips.length} trip{trips.length !== 1 ? 's' : ''} pending assignment
        </p>
        <button onClick={refetch} className="text-sm text-primary-600 hover:text-primary-700">
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {trips.map((trip) => (
          <TripCard
            key={trip.id}
            trip={trip}
            showAssignment
            projectOptions={buildProjectOptions(trip)}
            selectedProjectId={selectedProjects[trip.id] || ''}
            onProjectChange={(projectId) =>
              setSelectedProjects((prev) => ({
                ...prev,
                [trip.id]: projectId,
              }))
            }
            onAssign={() => handleAssign(trip.id)}
            onDismiss={() => handleDismiss(trip.id)}
            isProcessing={processingIds.has(trip.id)}
          />
        ))}
      </div>
    </div>
  );
}